import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { SettingsProvider } from "./context/SettingsContext";
import { Layout } from "./components/Layout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Toaster } from "./components/ui/sonner";

import Home from "./pages/Home";
import FindSession from "./pages/FindSession";
import SessionDetail from "./pages/SessionDetail";
import HowItWorks from "./pages/HowItWorks";
import PhotographerLanding from "./pages/PhotographerLanding";
import PhotographerDirectory from "./pages/PhotographerDirectory";
import PhotographerProfile from "./pages/PhotographerProfile";
import About from "./pages/About";
import FAQ from "./pages/FAQ";
import Contact from "./pages/Contact";
import Safety from "./pages/Safety";
import Stories from "./pages/Stories";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import StrangerRegister from "./pages/StrangerRegister";
import Dashboard from "./pages/Dashboard";
import SessionForm from "./pages/SessionForm";
import SessionManage from "./pages/SessionManage";
import BookingDetail from "./pages/BookingDetail";
import AdminLayout from "./pages/AdminLayout";
import { AdminDashboardHome } from "./components/admin/AdminDashboardHome";
import { UsersTab } from "./components/admin/UsersTab";
import { PhotographersTab } from "./components/admin/PhotographersTab";
import { StrangersTab } from "./components/admin/StrangersTab";
import { ApplicationsTab } from "./components/admin/ApplicationsTab";
import { MatchesTab } from "./components/admin/MatchesTab";
import UserDetail from "./pages/admin/UserDetail";
import PhotographerDetail from "./pages/admin/PhotographerDetail";
import StrangerDetail from "./pages/admin/StrangerDetail";
import { SessionsTab } from "./components/admin/SessionsTab";
import AdminSessionForm from "./pages/admin/AdminSessionForm";
import AdminSessionDetail from "./pages/admin/AdminSessionDetail";
import { RegistrationsTab } from "./components/admin/RegistrationsTab";
import { VerificationsTab } from "./components/admin/VerificationsTab";
import { PhotoReviewTab } from "./components/admin/PhotoReviewTab";
import { StoriesTab } from "./components/admin/StoriesTab";
import { SettingsTab } from "./components/admin/SettingsTab";
import { BookingFeesTab } from "./components/admin/BookingFeesTab";
import FeeDetail from "./pages/admin/FeeDetail";
import { MessagesTab } from "./components/admin/MessagesTab";
import { GalleriesTab } from "./components/admin/GalleriesTab";
import { SafetyTab } from "./components/admin/SafetyTab";
import SafetyReportDetail from "./pages/admin/SafetyReportDetail";
import { ContentTab } from "./components/admin/ContentTab";
import { AnalyticsTab } from "./components/admin/AnalyticsTab";
import { ModuleNotBuilt } from "./components/admin/ModuleNotBuilt";
import LegalPage from "./pages/LegalPage";
import NotFound from "./pages/NotFound";

import "./App.css";

const ADMIN_ROLES = ["admin", "super_admin"];

function App() {
  return (
    <BrowserRouter>
      <SettingsProvider>
        <AuthProvider>
          <Routes>
            <Route element={<Layout />}>
              <Route index element={<Home />} />
              <Route path="/find-a-session" element={<FindSession />} />
              <Route path="/sessions/:id" element={<SessionDetail />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/photographers" element={<PhotographerDirectory />} />
              <Route path="/photographers/join" element={<PhotographerLanding />} />
              <Route path="/photographers/:slug" element={<PhotographerProfile />} />
              <Route path="/about" element={<About />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/safety" element={<Safety />} />
              <Route path="/stories" element={<Stories />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/legal/:doc" element={<LegalPage />} />
              <Route
                path="/register"
                element={
                  <ProtectedRoute role="stranger">
                    <StrangerRegister />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/bookings/:id"
                element={
                  <ProtectedRoute role="stranger">
                    <BookingDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/sessions/new"
                element={
                  <ProtectedRoute role="photographer">
                    <SessionForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/sessions/:id/edit"
                element={
                  <ProtectedRoute role="photographer">
                    <SessionForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/sessions/:id/manage"
                element={
                  <ProtectedRoute role="photographer">
                    <SessionManage />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<NotFound />} />
            </Route>

            <Route
              path="/admin"
              element={
                <ProtectedRoute role={ADMIN_ROLES}>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboardHome />} />
              <Route path="users" element={<UsersTab />} />
              <Route path="users/:id" element={<UserDetail />} />
              <Route path="photographers" element={<PhotographersTab />} />
              <Route path="photographers/:id" element={<PhotographerDetail />} />
              <Route path="verification" element={<VerificationsTab />} />
              <Route path="photo-review" element={<PhotoReviewTab />} />
              <Route path="sessions" element={<SessionsTab />} />
              <Route path="sessions/new" element={<AdminSessionForm />} />
              <Route path="sessions/:id" element={<AdminSessionDetail />} />
              <Route path="sessions/:id/edit" element={<AdminSessionForm />} />
              <Route path="strangers" element={<StrangersTab />} />
              <Route path="strangers/:id" element={<StrangerDetail />} />
              <Route path="applications" element={<ApplicationsTab />} />
              <Route path="matches" element={<MatchesTab />} />
              <Route path="bookings" element={<RegistrationsTab />} />
              <Route path="payments" element={<BookingFeesTab />} />
              <Route path="payments/:id" element={<FeeDetail />} />
              <Route path="waivers" element={<ModuleNotBuilt name="Waivers" />} />
              <Route path="messages" element={<MessagesTab />} />
              <Route path="galleries" element={<GalleriesTab />} />
              <Route path="reviews" element={<StoriesTab />} />
              <Route path="safety" element={<SafetyTab />} />
              <Route path="safety/:id" element={<SafetyReportDetail />} />
              <Route path="content" element={<ContentTab />} />
              <Route path="analytics" element={<AnalyticsTab />} />
              <Route path="settings" element={<SettingsTab />} />
            </Route>
          </Routes>
          <Toaster />
        </AuthProvider>
      </SettingsProvider>
    </BrowserRouter>
  );
}

export default App;
